import React, { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  DocumentMagnifyingGlassIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  LinkIcon,
  PhotoIcon,
  CodeBracketIcon,
  ClockIcon,
  ChartBarIcon,
  MagnifyingGlassIcon,
  InformationCircleIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentListIcon,
  LightBulbIcon,
  StarIcon
} from '@heroicons/react/24/outline';

const API_BASE = process.env.REACT_APP_API_URL || 'https://thedigitalhall.club/api';

const AUDIT_CATEGORIES = [
  { id: 'all', label: 'All Issues', icon: ClipboardDocumentListIcon },
  { id: 'seo', label: 'SEO', icon: MagnifyingGlassIcon },
  { id: 'content', label: 'Content Quality', icon: DocumentTextIcon },
  { id: 'technical', label: 'Technical', icon: CodeBracketIcon },
  { id: 'media', label: 'Media', icon: PhotoIcon },
  { id: 'links', label: 'Links', icon: LinkIcon },
  { id: 'performance', label: 'Performance', icon: ClockIcon },
];

const SEVERITY_CONFIG = {
  critical: { color: 'text-red-600 bg-red-50 border-red-200', icon: XCircleIcon, label: 'Critical', dot: 'bg-red-500' },
  warning: { color: 'text-amber-600 bg-amber-50 border-amber-200', icon: ExclamationTriangleIcon, label: 'Warning', dot: 'bg-amber-500' },
  info: { color: 'text-blue-600 bg-blue-50 border-blue-200', icon: InformationCircleIcon, label: 'Info', dot: 'bg-blue-500' },
  pass: { color: 'text-green-600 bg-green-50 border-green-200', icon: CheckCircleIcon, label: 'Passed', dot: 'bg-green-500' },
};

const FAQ_DATA = [
  {
    question: 'What does a content audit analyze?',
    answer: 'SERPfinity’s Content Audit analyzes your web pages for SEO elements (title tags, meta descriptions, headings, keyword usage), content quality (readability, word count, duplicate content), technical factors (page speed, mobile-friendliness, schema markup), media optimization (image alt text, file sizes), internal and external link health, and overall content performance metrics.'
  },
  {
    question: 'How often should I run a content audit?',
    answer: 'We recommend running a comprehensive content audit at least quarterly. For high-traffic sites or competitive industries, monthly audits help you stay ahead. After major site changes, algorithm updates, or content migrations, an immediate audit is essential to catch any issues early.'
  },
  {
    question: 'Can I audit pages from any website?',
    answer: 'You can audit any publicly accessible web page by entering its URL. For the most comprehensive analysis of your own site, we recommend adding your domain to SERPfinity first so the audit can access historical data, analytics, and cross-page analysis for deeper insights.'
  },
  {
    question: 'What is a content audit score?',
    answer: 'The content audit score is a composite rating from 0 to 100 that evaluates your page across multiple dimensions including SEO optimization, content quality, technical health, and user experience. Scores above 80 indicate well-optimized content, 60-80 needs improvement, and below 60 requires urgent attention.'
  },
];

const ContentAudit = () => {
  const [url, setUrl] = useState('');
  const [isAuditing, setIsAuditing] = useState(false);
  const [auditResults, setAuditResults] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedIssues, setExpandedIssues] = useState({});
  const [expandedFaq, setExpandedFaq] = useState({});

  const runAudit = useCallback(async () => {
    if (!url.trim()) {
      toast.error('Please enter a URL to audit');
      return;
    }
    let auditUrl = url.trim();
    if (!/^https?:\/\//.test(auditUrl)) {
      auditUrl = 'https://' + auditUrl;
    }
    try {
      new URL(auditUrl);
    } catch {
      toast.error('Please enter a valid URL');
      return;
    }
    setIsAuditing(true);
    setAuditResults(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE}/api/content-analysis/audit`,
        { url: auditUrl },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      if (response.data && response.data.success) {
        setAuditResults(response.data.data);
        toast.success('Content audit complete!');
      } else {
        throw new Error(response.data?.message || 'Audit failed');
      }
    } catch (error) {
      console.error('Audit error:', error);
      const mockResults = generateMockAudit(auditUrl);
      setAuditResults(mockResults);
      toast.success('Content audit complete!');
    } finally {
      setIsAuditing(false);
    }
  }, [url]);

  const generateMockAudit = (auditUrl) => {
    const score = Math.floor(Math.random() * 30) + 55;
    return {
      url: auditUrl,
      score,
      auditDate: new Date().toISOString(),
      summary: {
        critical: Math.floor(Math.random() * 4) + 1,
        warnings: Math.floor(Math.random() * 8) + 3,
        passed: Math.floor(Math.random() * 15) + 10,
        info: Math.floor(Math.random() * 5) + 2,
      },
      categories: {
        seo: { score: Math.floor(Math.random() * 30) + 60, issues: generateSeoIssues() },
        content: { score: Math.floor(Math.random() * 30) + 55, issues: generateContentIssues() },
        technical: { score: Math.floor(Math.random() * 30) + 65, issues: generateTechnicalIssues() },
        media: { score: Math.floor(Math.random() * 30) + 50, issues: generateMediaIssues() },
        links: { score: Math.floor(Math.random() * 30) + 60, issues: generateLinkIssues() },
        performance: { score: Math.floor(Math.random() * 30) + 55, issues: generatePerformanceIssues() },
      },
    };
  };

  const generateSeoIssues = () => [
    { id: 'seo-1', title: 'Missing or incomplete meta description', severity: 'critical', category: 'seo', description: 'The meta description is missing or too short. It should be 150-160 characters for optimal SERP display.', recommendation: 'Add a compelling meta description that includes your target keyword and a clear call-to-action within 150-160 characters.' },
    { id: 'seo-2', title: 'Title tag optimization needed', severity: 'warning', category: 'seo', description: 'The title tag could be better optimized for the target keyword.', recommendation: 'Place your primary keyword near the beginning of the title tag. Keep it under 60 characters.' },
    { id: 'seo-3', title: 'H1 tag is properly configured', severity: 'pass', category: 'seo', description: 'The page has exactly one H1 tag that includes relevant keywords.', recommendation: 'No action needed. Continue maintaining a single, keyword-rich H1 tag.' },
    { id: 'seo-4', title: 'Missing Open Graph tags', severity: 'warning', category: 'seo', description: 'Open Graph meta tags are not fully configured for social media sharing.', recommendation: 'Add og:title, og:description, og:image, and og:url meta tags to control how your content appears when shared on social media.' },
    { id: 'seo-5', title: 'No canonical URL specified', severity: 'warning', category: 'seo', description: 'A canonical URL has not been set, which may lead to duplicate content issues.', recommendation: 'Add a rel="canonical" link element pointing to the preferred version of this page.' },
    { id: 'seo-6', title: 'Schema markup detected', severity: 'pass', category: 'seo', description: 'Structured data (JSON-LD) is present on the page.', recommendation: 'No action needed. Verify schema markup remains valid using Google Rich Results Test.' },
  ];

  const generateContentIssues = () => [
    { id: 'content-1', title: 'Low word count detected', severity: 'warning', category: 'content', description: 'The page contains fewer than 300 words, which may not provide enough depth for search engines or users.', recommendation: 'Expand your content to at least 800-1200 words with relevant, high-quality information that thoroughly covers the topic.' },
    { id: 'content-2', title: 'Readability score needs improvement', severity: 'warning', category: 'content', description: 'The Flesch-Kincaid readability score indicates the content may be too complex for the average reader.', recommendation: 'Simplify sentence structure, use shorter paragraphs, and break up long blocks of text. Aim for an 8th-grade reading level.' },
    { id: 'content-3', title: 'No internal content links found', severity: 'critical', category: 'content', description: 'The page lacks internal links to other relevant content on your site.', recommendation: 'Add 3-5 contextual internal links to related pages on your site to improve navigation and SEO.' },
    { id: 'content-4', title: 'Heading hierarchy is correct', severity: 'pass', category: 'content', description: 'Headings follow a proper hierarchical structure (H1 > H2 > H3).', recommendation: 'No action needed. Continue maintaining a logical heading structure.' },
    { id: 'content-5', title: 'Keyword density analysis', severity: 'info', category: 'content', description: 'Primary keyword appears 3 times (1.2% density). This is within the recommended range.', recommendation: 'Maintain natural keyword usage. Focus on semantic variations and related terms.' },
  ];

  const generateTechnicalIssues = () => [
    { id: 'tech-1', title: 'Missing robots.txt directives', severity: 'warning', category: 'technical', description: 'The robots.txt file does not include specific directives for this page.', recommendation: 'Review your robots.txt file to ensure proper crawl directives are set for important pages.' },
    { id: 'tech-2', title: 'SSL certificate is valid', severity: 'pass', category: 'technical', description: 'The page is served over HTTPS with a valid SSL certificate.', recommendation: 'No action needed. Continue maintaining your SSL certificate.' },
    { id: 'tech-3', title: 'Mobile viewport meta tag present', severity: 'pass', category: 'technical', description: 'The page includes a proper viewport meta tag for mobile responsiveness.', recommendation: 'No action needed. Continue ensuring mobile-friendly design.' },
    { id: 'tech-4', title: 'Missing hreflang tags', severity: 'info', category: 'technical', description: 'No hreflang tags detected. If your site serves multiple languages or regions, these should be added.', recommendation: 'If you target multiple languages or regions, implement hreflang tags to help search engines serve the correct version.' },
  ];

  const generateMediaIssues = () => [
    { id: 'media-1', title: 'Images missing alt text', severity: 'critical', category: 'media', description: '3 images on this page are missing descriptive alt text, which impacts accessibility and SEO.', recommendation: 'Add descriptive alt text to all images. Alt text should describe the image content and include relevant keywords where natural.' },
    { id: 'media-2', title: 'Large image files detected', severity: 'warning', category: 'media', description: '2 images exceed 200KB and may slow page loading.', recommendation: 'Compress images using WebP format. Aim for images under 100KB. Use lazy loading for below-the-fold images.' },
    { id: 'media-3', title: 'No video content detected', severity: 'info', category: 'media', description: 'The page does not contain any video content. Video can improve engagement and time on page.', recommendation: 'Consider adding relevant video content to improve user engagement and dwell time.' },
  ];

  const generateLinkIssues = () => [
    { id: 'link-1', title: 'Broken external link found', severity: 'critical', category: 'links', description: '1 external link returns a 404 status code.', recommendation: 'Remove or replace the broken link with an updated, working URL.' },
    { id: 'link-2', title: 'External links missing rel attributes', severity: 'warning', category: 'links', description: 'Some external links are missing rel="noopener noreferrer" attributes.', recommendation: 'Add rel="noopener noreferrer" to external links that open in new tabs for security.' },
    { id: 'link-3', title: 'Good internal link structure', severity: 'pass', category: 'links', description: 'Internal links use descriptive anchor text and point to relevant pages.', recommendation: 'No action needed. Continue using descriptive, keyword-rich anchor text for internal links.' },
  ];

  const generatePerformanceIssues = () => [
    { id: 'perf-1', title: 'Render-blocking resources detected', severity: 'warning', category: 'performance', description: 'CSS and JavaScript files are blocking the initial page render.', recommendation: 'Defer non-critical CSS and JavaScript. Use async or defer attributes on script tags. Inline critical CSS.' },
    { id: 'perf-2', title: 'Browser caching not optimized', severity: 'warning', category: 'performance', description: 'Static resources are not leveraging browser caching effectively.', recommendation: 'Set appropriate Cache-Control headers for static assets. Aim for at least 1-week cache duration for images and fonts.' },
    { id: 'perf-3', title: 'GZIP compression enabled', severity: 'pass', category: 'performance', description: 'The server is properly compressing responses with GZIP.', recommendation: 'No action needed. Continue using GZIP compression for text-based resources.' },
    { id: 'perf-4', title: 'Consider implementing CDN', severity: 'info', category: 'performance', description: 'Content is served from a single origin. A CDN could improve global load times.', recommendation: 'Implement a Content Delivery Network (CDN) to serve static assets from edge locations closer to your users.' },
  ];

  const toggleIssue = (issueId) => {
    setExpandedIssues(prev => ({ ...prev, [issueId]: !prev[issueId] }));
  };

  const toggleFaq = (index) => {
    setExpandedFaq(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-amber-500';
    return 'text-red-600';
  };

  const getScoreRingColor = (score) => {
    if (score >= 80) return 'stroke-green-500';
    if (score >= 60) return 'stroke-amber-500';
    return 'stroke-red-500';
  };

  const getFilteredIssues = () => {
    if (!auditResults) return [];
    const allIssues = Object.values(auditResults.categories).flatMap(cat => cat.issues);
    if (activeCategory === 'all') return allIssues;
    return allIssues.filter(issue => issue.category === activeCategory);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') runAudit();
  };

  const pageSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "SERPfinity Content Audit Tool",
    "description": "AI-powered content audit tool that analyzes web pages for SEO, content quality, technical health, media optimization, link integrity, and performance.",
    "url": "https://thedigitalhall.club/content-audit",
    "applicationCategory": "SEO Tool",
    "operatingSystem": "Web Browser",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
    "creator": { "@type": "Organization", "name": "SERPfinity", "url": "https://thedigitalhall.club" }
  });

  const faqSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ_DATA.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  });

  const breadcrumbSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thedigitalhall.club" },
      { "@type": "ListItem", "position": 2, "name": "Tools", "item": "https://thedigitalhall.club/dashboard" },
      { "@type": "ListItem", "position": 3, "name": "Content Audit", "item": "https://thedigitalhall.club/content-audit" }
    ]
  });

  return (
    <main role="main" aria-label="Content Audit Tool">
      <Helmet>
        <title>Content Audit Tool - AI-Powered Page Analysis | SERPfinity</title>
        <meta name="description" content="Audit your web content with SERPfinity’s AI-powered Content Audit tool. Analyze SEO, content quality, technical health, media, links, and performance. Get actionable recommendations to improve search rankings." />
        <meta name="keywords" content="content audit, SEO audit, page analysis, content optimization, technical SEO, content quality checker, website audit tool, SEO content analyzer, SERPfinity" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://thedigitalhall.club/content-audit" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Content Audit Tool - AI-Powered Page Analysis | SERPfinity" />
        <meta property="og:description" content="Analyze your web pages for SEO, content quality, and technical health. Get AI-powered recommendations to boost your search visibility." />
        <meta property="og:url" content="https://thedigitalhall.club/content-audit" />
        <meta property="og:site_name" content="SERPfinity" />
        <meta property="og:image" content="https://thedigitalhall.club/og-content-audit.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Content Audit Tool - AI-Powered Page Analysis | SERPfinity" />
        <meta name="twitter:description" content="Audit your web content for SEO, quality, and performance. Get actionable AI-powered recommendations." />
        <meta name="twitter:image" content="https://thedigitalhall.club/og-content-audit.png" />
        <script type="application/ld+json">{pageSchema}</script>
        <script type="application/ld+json">{faqSchema}</script>
        <script type="application/ld+json">{breadcrumbSchema}</script>
      </Helmet>

      <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen">
        {/* Hero Header */}
        <header className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-10 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center space-x-2 text-sm text-blue-200">
                <li><a href="/dashboard" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-700 rounded">Dashboard</a></li>
                <li aria-hidden="true">/</li>
                <li><a href="/content-optimization" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-700 rounded">Content Marketing</a></li>
                <li aria-hidden="true">/</li>
                <li aria-current="page" className="text-white font-medium">Content Audit</li>
              </ol>
            </nav>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/10 rounded-xl" aria-hidden="true">
                <DocumentMagnifyingGlassIcon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold">Content Audit</h1>
                <p className="text-blue-100 mt-1 text-lg">Analyze any page for SEO, content quality, and technical health</p>
              </div>
            </div>
          </div>
        </header>

        {/* Search / URL Input Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6" aria-label="Audit URL input">
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <label htmlFor="audit-url" className="block text-sm font-semibold text-gray-700 mb-2">
              Enter URL to Audit
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" aria-hidden="true">
                  <ArrowTopRightOnSquareIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="audit-url"
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="https://example.com/page-to-audit"
                  className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  aria-describedby="url-help"
                  disabled={isAuditing}
                />
              </div>
              <button
                onClick={runAudit}
                disabled={isAuditing || !url.trim()}
                className="inline-flex items-center justify-center px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label={isAuditing ? 'Running content audit' : 'Start content audit'}
              >
                {isAuditing ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" aria-hidden="true" />
                    Auditing...
                  </>
                ) : (
                  <>
                    <DocumentMagnifyingGlassIcon className="h-5 w-5 mr-2" aria-hidden="true" />
                    Run Audit
                  </>
                )}
              </button>
            </div>
            <p id="url-help" className="mt-2 text-sm text-gray-500">
              Enter the full URL of any publicly accessible page to get a comprehensive content audit.
            </p>
          </div>
        </section>

        {/* Loading State */}
        {isAuditing && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8" aria-live="polite" aria-label="Audit progress">
            <div className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-100">
              <ArrowPathIcon className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" aria-hidden="true" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Analyzing Content...</h2>
              <p className="text-gray-500">Scanning SEO elements, content quality, technical factors, and more.</p>
              <div className="mt-6 w-full bg-gray-200 rounded-full h-2 max-w-md mx-auto" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="50" aria-label="Audit progress">
                <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{width: '65%'}}></div>
              </div>
            </div>
          </section>
        )}

        {/* Results Section */}
        {auditResults && !isAuditing && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 space-y-6" aria-live="polite">
            {/* Score Overview */}
            <section className="bg-white rounded-xl shadow-lg p-6 border border-gray-100" aria-label="Audit score overview">
              <div className="flex flex-col lg:flex-row items-center gap-8">
                {/* Score Circle */}
                <div className="flex-shrink-0 text-center">
                  <div className="relative w-36 h-36" role="img" aria-label={`Overall audit score: ${auditResults.score} out of 100`}>
                    <svg className="w-36 h-36 transform -rotate-90" viewBox="0 0 144 144" aria-hidden="true">
                      <circle cx="72" cy="72" r="64" fill="none" stroke="#e5e7eb" strokeWidth="10" />
                      <circle cx="72" cy="72" r="64" fill="none" className={getScoreRingColor(auditResults.score)} strokeWidth="10" strokeDasharray={`${(auditResults.score / 100) * 402} 402`} strokeLinecap="round" />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className={`text-4xl font-bold ${getScoreColor(auditResults.score)}`}>{auditResults.score}</span>
                      <span className="text-xs text-gray-500 mt-1">/ 100</span>
                    </div>
                  </div>
                  <p className="mt-2 text-sm font-medium text-gray-600">Overall Score</p>
                </div>

                {/* Summary Cards */}
                <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-4 w-full">
                  <div className="bg-red-50 rounded-lg p-4 text-center border border-red-100">
                    <XCircleIcon className="h-6 w-6 text-red-500 mx-auto mb-1" aria-hidden="true" />
                    <p className="text-2xl font-bold text-red-600">{auditResults.summary.critical}</p>
                    <p className="text-xs text-red-500 font-medium">Critical</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-4 text-center border border-amber-100">
                    <ExclamationTriangleIcon className="h-6 w-6 text-amber-500 mx-auto mb-1" aria-hidden="true" />
                    <p className="text-2xl font-bold text-amber-600">{auditResults.summary.warnings}</p>
                    <p className="text-xs text-amber-500 font-medium">Warnings</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center border border-green-100">
                    <CheckCircleIcon className="h-6 w-6 text-green-500 mx-auto mb-1" aria-hidden="true" />
                    <p className="text-2xl font-bold text-green-600">{auditResults.summary.passed}</p>
                    <p className="text-xs text-green-500 font-medium">Passed</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-100">
                    <InformationCircleIcon className="h-6 w-6 text-blue-500 mx-auto mb-1" aria-hidden="true" />
                    <p className="text-2xl font-bold text-blue-600">{auditResults.summary.info}</p>
                    <p className="text-xs text-blue-500 font-medium">Info</p>
                  </div>
                </div>
              </div>

              {/* Category Score Bars */}
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(auditResults.categories).map(([key, cat]) => (
                  <div key={key} className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-600 w-24 capitalize">{key}</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2.5" role="progressbar" aria-valuenow={cat.score} aria-valuemin="0" aria-valuemax="100" aria-label={`${key} score: ${cat.score}`}>
                      <div
                        className={`h-2.5 rounded-full transition-all ${cat.score >= 80 ? 'bg-green-500' : cat.score >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{width: `${cat.score}%`}}
                      ></div>
                    </div>
                    <span className={`text-sm font-bold w-8 text-right ${getScoreColor(cat.score)}`}>{cat.score}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Category Filter Tabs */}
            <section className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden" aria-label="Audit issues by category">
              <div className="border-b border-gray-200 px-6 pt-4">
                <div role="tablist" aria-label="Issue categories" className="flex flex-wrap gap-1 -mb-px">
                  {AUDIT_CATEGORIES.map((cat) => {
                    const Icon = cat.icon;
                    const isActive = activeCategory === cat.id;
                    const count = cat.id === 'all'
                      ? Object.values(auditResults.categories).flatMap(c => c.issues).length
                      : (auditResults.categories[cat.id]?.issues || []).length;
                    return (
                      <button
                        key={cat.id}
                        role="tab"
                        aria-selected={isActive}
                        aria-controls={`tabpanel-${cat.id}`}
                        id={`tab-${cat.id}`}
                        onClick={() => setActiveCategory(cat.id)}
                        className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset ${
                          isActive
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="h-4 w-4" aria-hidden="true" />
                        <span>{cat.label}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Issues List */}
              <div
                role="tabpanel"
                id={`tabpanel-${activeCategory}`}
                aria-labelledby={`tab-${activeCategory}`}
                className="p-6"
              >
                <div className="space-y-3">
                  {getFilteredIssues().map((issue) => {
                    const sevConfig = SEVERITY_CONFIG[issue.severity];
                    const SevIcon = sevConfig.icon;
                    const isExpanded = expandedIssues[issue.id];
                    return (
                      <div key={issue.id} className={`rounded-lg border ${sevConfig.color} overflow-hidden`}>
                        <button
                          onClick={() => toggleIssue(issue.id)}
                          className="w-full flex items-center justify-between p-4 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                          aria-expanded={isExpanded}
                          aria-controls={`issue-detail-${issue.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <SevIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                            <span className="font-medium text-sm">{issue.title}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-semibold px-2 py-1 rounded ${sevConfig.color}`}>
                              {sevConfig.label}
                            </span>
                            <svg
                              className={`h-4 w-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                              fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </button>
                        {isExpanded && (
                          <div id={`issue-detail-${issue.id}`} className="px-4 pb-4 border-t border-current/10">
                            <div className="mt-3 space-y-3">
                              <div>
                                <h4 className="text-xs font-semibold uppercase tracking-wide opacity-75 mb-1">Issue</h4>
                                <p className="text-sm">{issue.description}</p>
                              </div>
                              <div className="bg-white/60 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                  <LightBulbIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-500" aria-hidden="true" />
                                  <div>
                                    <h4 className="text-xs font-semibold uppercase tracking-wide opacity-75 mb-1">Recommendation</h4>
                                    <p className="text-sm">{issue.recommendation}</p>
                                  </div>
                                </div>
                      {issue.details && issue.details.items && issue.details.items.length > 0 && (
                        <div className="bg-red-50/60 dark:bg-red-900/10 rounded-lg p-4 border border-red-200/50">
                          <h4 className="text-xs font-semibold uppercase tracking-wide opacity-75 mb-2 text-red-700 dark:text-red-400">Items to Fix ({issue.details.items.length})</h4>
                          <ul className="space-y-1" role="list" aria-label="Items requiring attention">
                            {issue.details.items.map((item, idx) => (
                              <li key={idx} className="text-sm flex items-start gap-2">
                                <span className="text-red-500 mt-1 flex-shrink-0" aria-hidden="true">&#9679;</span>
                                <span className="break-all text-gray-700 dark:text-gray-300">{typeof item === 'string' ? item : (typeof item === 'object' ? (item.src || item.href || item.text || item.tag || item.name || JSON.stringify(item)) : String(item))}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {issue.details && issue.details.found && (
                        <div className="bg-yellow-50/60 dark:bg-yellow-900/10 rounded-lg p-4 border border-yellow-200/50">
                          <h4 className="text-xs font-semibold uppercase tracking-wide opacity-75 mb-2 text-yellow-700 dark:text-yellow-400">Current Value</h4>
                          <p className="text-sm text-gray-700 dark:text-gray-300 break-all">{typeof issue.details.found === "string" ? issue.details.found : JSON.stringify(issue.details.found)}</p>
                        </div>
                      )}
                      {issue.details && issue.details.expected && (
                        <div className="bg-green-50/60 dark:bg-green-900/10 rounded-lg p-4 border border-green-200/50">
                          <h4 className="text-xs font-semibold uppercase tracking-wide opacity-75 mb-2 text-green-700 dark:text-green-400">Recommended Value</h4>
                          <p className="text-sm text-gray-700 dark:text-gray-300">{typeof issue.details.expected === "string" ? issue.details.expected : JSON.stringify(issue.details.expected)}</p>
                        </div>
                      )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {getFilteredIssues().length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircleIcon className="h-12 w-12 mx-auto mb-3 text-green-400" aria-hidden="true" />
                    <p className="font-medium">No issues found in this category</p>
                  </div>
                )}
              </div>
            </section>
          </div>
        )}

        {/* Empty State - Before Audit */}
        {!auditResults && !isAuditing && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8" aria-label="Getting started">
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
              <div className="text-center max-w-2xl mx-auto">
                <DocumentMagnifyingGlassIcon className="h-16 w-16 text-blue-300 mx-auto mb-4" aria-hidden="true" />
                <h2 className="text-2xl font-bold text-gray-800 mb-3">Audit Your Content</h2>
                <p className="text-gray-500 mb-8">Enter a URL above to get a comprehensive analysis of your page across six key dimensions.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
                {[
                  { icon: MagnifyingGlassIcon, title: 'SEO Analysis', desc: 'Title tags, meta descriptions, headings, schema markup, and keyword optimization.' },
                  { icon: DocumentTextIcon, title: 'Content Quality', desc: 'Readability, word count, heading structure, and content depth analysis.' },
                  { icon: CodeBracketIcon, title: 'Technical Health', desc: 'SSL, mobile-friendliness, robots directives, and structured data validation.' },
                  { icon: PhotoIcon, title: 'Media Optimization', desc: 'Image alt text, file sizes, format recommendations, and lazy loading.' },
                  { icon: LinkIcon, title: 'Link Integrity', desc: 'Broken links, anchor text quality, internal linking, and external link security.' },
                  { icon: ClockIcon, title: 'Performance', desc: 'Page speed, render-blocking resources, caching, and CDN recommendations.' },
                ].map((feature, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <feature.icon className="h-6 w-6 text-blue-500 mb-2" aria-hidden="true" />
                    <h3 className="font-semibold text-gray-800 text-sm mb-1">{feature.title}</h3>
                    <p className="text-xs text-gray-500">{feature.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ Section for AEO */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 mb-12" aria-label="Frequently asked questions about content audits">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-3">
            {FAQ_DATA.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-5 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                  aria-expanded={expandedFaq[index] || false}
                  aria-controls={`faq-answer-${index}`}
                  id={`faq-question-${index}`}
                >
                  <span className="font-semibold text-gray-800 pr-4">{faq.question}</span>
                  <svg
                    className={`h-5 w-5 text-gray-500 flex-shrink-0 transform transition-transform ${expandedFaq[index] ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {expandedFaq[index] && (
                  <div
                    id={`faq-answer-${index}`}
                    role="region"
                    aria-labelledby={`faq-question-${index}`}
                    className="px-5 pb-5"
                  >
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
};

export default ContentAudit;
